/**
 * Acciones que cambian la colección: abrir sobres y fusionar.
 *
 * Ninguna escribe en tablas. Las dos llaman a funciones RPC, y no por elegancia:
 * el cliente no tiene permiso de escritura sobre `user_cards` ni sobre
 * `profiles.gold`, así que una mutación normal sería rechazada por la base. Todo
 * lo que crea o destruye valor ocurre en el servidor, en una transacción.
 *
 * Fíjate en que ninguna manda el id del usuario. Lo saca el servidor de
 * `auth.uid()`: si viajara como parámetro, cualquiera podría fusionar las cartas
 * de otro.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/services/queryClient';
import { describeDatabaseError, requireSupabase } from '@/services/supabaseClient';
import type { DbUserCard } from '@/types/database';
import type { CollectionCard } from '@/types/game';

function toCollectionCard(row: DbUserCard): CollectionCard {
  return {
    id: row.id,
    masterId: row.card_master_id,
    level: row.level,
    xp: row.xp,
    isTradable: row.is_tradable,
    serialNumber: row.serial_number,
    acquiredAt: row.created_at,
  };
}

/** Mensaje de PostgreSQL tal cual si viene de un RAISE nuestro; traducido si no. */
function toReadableError(error: { code?: string; message: string }): Error {
  // Las funciones RPC lanzan mensajes ya escritos para el jugador ("Te faltan
  // 120 de oro"). Traducirlos otra vez los convertiría en "No tienes permiso".
  const isOurs = error.code === 'P0001' || /^[A-ZÁÉÍÓÚÑ]/.test(error.message);
  return new Error(isOurs ? error.message : describeDatabaseError(error));
}

export function useOpenPack(userId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (packId: string): Promise<CollectionCard[]> => {
      const { data, error } = await requireSupabase().rpc('open_pack', { p_pack_id: packId });
      if (error) throw toReadableError(error);
      return ((data ?? []) as DbUserCard[]).map(toCollectionCard);
    },
    onSuccess: () => {
      // El sobre cambia inventario y saldo a la vez; si solo se invalidara uno,
      // la cabecera enseñaría oro que ya no existe.
      if (!userId) return;
      void queryClient.invalidateQueries({ queryKey: queryKeys.inventory(userId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.profile(userId) });
    },
  });
}

export function useFuseCards(userId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (cardIds: string[]): Promise<CollectionCard> => {
      const { data, error } = await requireSupabase().rpc('fuse_user_cards', {
        p_card_ids: cardIds,
      });
      if (error) throw toReadableError(error);
      return toCollectionCard(data as unknown as DbUserCard);
    },
    onSuccess: () => {
      if (!userId) return;
      void queryClient.invalidateQueries({ queryKey: queryKeys.inventory(userId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.profile(userId) });
      // El mazo también: la carta superviviente sigue en él, pero las dos
      // destruidas podrían haber estado en otro mazo del jugador.
      void queryClient.invalidateQueries({ queryKey: queryKeys.activeDeck(userId) });
    },
  });
}
